class ProductImagesSlider extends HTMLElement {
  constructor() {
    super();
    this.mainSwiper = null;
    this.navSwiper = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Ensure Swiper is loaded
    if (typeof Swiper === 'undefined') {
      window.addEventListener('load', () => this.init());
      return;
    }

    const mainContainer = this.querySelector('.swiper-main');
    const navContainer = this.querySelector('.swiper-nav');

    if (!mainContainer || !navContainer) return;

    // Destroy existing instances to be safe
    if (this.mainSwiper) this.mainSwiper.destroy(true, true);
    if (this.navSwiper) this.navSwiper.destroy(true, true);

    // Initialize Nav Swiper
    this.navSwiper = new Swiper(navContainer, {
      direction: 'vertical',
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: true,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      threshold: 5,
      slideToClickedSlide: true,
    });

    // Initialize Main Swiper
    this.mainSwiper = new Swiper(mainContainer, {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 750,
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev'),
      },
      thumbs: {
        swiper: this.navSwiper,
      },
      observer: true,
      observeParents: true,
    });

    // Sync Highlight
    this.mainSwiper.on('slideChange', () => {
      this.updateHighlight();
    });

    // Initial Highlight
    this.updateHighlight();

    // Mobile Highlight Logic
    this.initMobileHighlight();
  }

  updateHighlight() {
    if (!this.navSwiper || !this.mainSwiper) return;
    
    const activeIndex = this.mainSwiper.activeIndex;
    const navSlides = this.navSwiper.slides;
    const activeSlide = navSlides[activeIndex];

    if (activeSlide) {
      const sliderRoot = this.closest('[data-slider]') || this;
      const highlight = sliderRoot.querySelector('.slider-highlight');
      if (highlight) {
        // Calculate position relative to the container
        const slideTop = activeSlide.offsetTop;
        const slideHeight = activeSlide.offsetHeight;
        
        highlight.style.top = `${slideTop}px`;
        highlight.style.height = `${slideHeight}px`;
      }
      
      // Also scroll the nav swiper to keep active slide in view if needed
      // Swiper's 'thumbs' module usually handles this, but custom centering might be needed
      this.navSwiper.slideTo(activeIndex);
      
      const container = this.querySelector('.slider-nav__container');
      if (container) {
        const currentScroll = container.scrollTop;
        const containerTop = container.getBoundingClientRect().top;
        const slideTopRel = activeSlide.getBoundingClientRect().top - containerTop + currentScroll;
        const slideBottomRel = slideTopRel + activeSlide.offsetHeight;
        const viewTop = currentScroll;
        const viewBottom = currentScroll + container.clientHeight;
        if (slideTopRel < viewTop || slideBottomRel > viewBottom) {
          const target = slideTopRel - (container.clientHeight - activeSlide.offsetHeight) / 2;
          container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
        }
      }
    }
  }

  initMobileHighlight() {
    const sliderRoot = this.closest('[data-slider]') || this;
    const mobileHighlight = sliderRoot.querySelector('.mobile-slider-highlight');
    const mobileHighlightContainer = sliderRoot.querySelector('.mobile-slider-highlight-container');
    
    if (mobileHighlight && mobileHighlightContainer && this.mainSwiper) {
      const updateMobile = () => {
        const itemCount = this.mainSwiper.slides.length;
        if (itemCount > 0) {
          const containerWidth = mobileHighlightContainer.offsetWidth;
          const highlightWidth = containerWidth / itemCount;
          const currentIndex = this.mainSwiper.activeIndex;
          
          mobileHighlight.style.width = `${highlightWidth}px`;
          mobileHighlight.style.transform = `translateX(${highlightWidth * currentIndex}px)`;
        }
      };

      this.mainSwiper.on('slideChange', updateMobile);
      this.mainSwiper.on('resize', updateMobile);
      updateMobile();
    }
  }

  // Method called by global.js when variant changes
  reInit(currentVarName, productName) {
    // Since global.js replaces the innerHTML of the sliders, 
    // we just need to re-initialize Swiper on the new DOM elements.
    // The new HTML is already filtered by the server (liquid), 
    // so we don't need to implement manual JS filtering here 
    // (unless the server logic is different from what we expect, but liquid looked correct).
    
    // Slight delay to ensure DOM is ready if needed, but usually synchronous update is fine.
    this.init();
  }
}

customElements.define('product-images-slider', ProductImagesSlider);
